import React, { Component, ReactNode } from "react";
import { logger } from "@/utils/logger";
import { captureException } from "@/lib/sentry";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";

interface Props {
  children: ReactNode;
  onReset?: () => void;
}

interface State {
  hasError: boolean;
  error?: Error;
}

export class TourErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    logger.error("Tour error:", error, { componentStack: errorInfo.componentStack });
    captureException(error, {
      context: "tour_error_boundary",
      componentStack: errorInfo.componentStack,
    });
  }

  handleReset = () => {
    this.setState({ hasError: false, error: undefined });
    this.props.onReset?.();
  };

  render() {
    if (this.state.hasError) {
      return (
        <Alert variant="destructive" className="fixed bottom-4 right-4 w-96 z-[1001]">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Tour Error</AlertTitle>
          <AlertDescription className="space-y-3">
            <p className="text-sm">
              The tour encountered an error. You can continue using the app normally.
            </p>
            {import.meta.env.DEV && this.state.error && (
              <p className="text-xs font-mono opacity-70">
                {this.state.error.message}
              </p>
            )}
            <Button 
              size="sm" 
              variant="outline" 
              onClick={this.handleReset}
              className="w-full"
            >
              Dismiss
            </Button>
          </AlertDescription>
        </Alert>
      );
    }

    return this.props.children;
  }
}
